// frontend/src/pages/FabricOriginSearch.jsx
import React, { useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, TextInput } from "../ui/UIComponents"; // use global UI

export default function FabricOriginSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [data, setData] = useState({ ok: true, count: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);

  const canSearch = q.trim().length >= 2;

  const hint = useMemo(() => {
    if (!canSearch) return "Nhập ít nhất 2 ký tự rồi nhấn Tìm";
    if (loading) return "Đang tìm…";
    if (err) return err;
    if (data?.count === 0 && data?.items?.length === 0) return "—";
    return `${data.count} kết quả`;
  }, [canSearch, loading, err, data]); // original behavior :contentReference[oaicite:4]{index=4}

  async function runSearch() {
    setErr("");
    if (!canSearch) {
      setData({ ok: true, count: 0, items: [] });
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`/api/searchorigin?q=${encodeURIComponent(q)}&limit=50`, {
        cache: "no-store",
      });
      const json = await resp.json();
      setData(json);
      setActiveIndex(-1);
    } catch (e) {
      setErr("Lỗi mạng");
    } finally {
      setLoading(false);
    }
  }

  function onSubmit(e) {
    e.preventDefault();
    runSearch();
  }

  const onKeyDownList = (e) => {
    if (!data?.items?.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, data.items.length - 1));
      scrollActiveIntoView();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      scrollActiveIntoView();
    }
  };

  const scrollActiveIntoView = () => {
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
      el?.scrollIntoView({ block: "nearest" });
    });
  };

  return (
    <div className="app-page">
      <main className="container" style={styles.container}>
        <header style={styles.header}>
          <div style={{ marginBottom: 16 }}>
            <Button type="button" onClick={() => navigate(-1)}>
              ← Trở về / Back
            </Button>
          </div>
          <h2 style={styles.h2}>Tìm kiếm nguồn gốc vải (HD)</h2>
          <p style={styles.sub}>
            Gõ từ khóa (ít nhất 2 ký tự), sau đó nhấn <b>Tìm</b> hoặc Enter.
          </p>
        </header>

        <form style={styles.searchWrap} onSubmit={onSubmit}>
          <div style={{ position: "relative", display: "flex", gap: 8 }}>
            {/* swapped native input -> global TextInput (UI-only change) */}
            <TextInput
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ví dụ: Italy, Velvet, C22 - 953, …"
              aria-label="Search Fabric Origin"
              autoFocus
              style={styles.input}
            />
            {q && (
              <Button
                type="button"
                aria-label="Xóa"
                onClick={() => {
                  setQ("");
                  setData({ ok: true, count: 0, items: [] });
                  setActiveIndex(-1);
                }}
                style={styles.clearBtn}
                title="Xóa ô tìm kiếm"
              >
                ×
              </Button>
            )}
            <Button
              type="submit"
              style={{ ...styles.searchBtn, ...(canSearch ? {} : styles.searchBtnDisabled) }}
              disabled={!canSearch || loading}
            >
              {loading ? "Đang tìm…" : "Tìm"}
            </Button>
          </div>
          <div style={styles.hint}>{hint}</div>
        </form>

        <section
          role="listbox"
          aria-label="Kết quả"
          tabIndex={0}
          style={styles.listbox}
          ref={listRef}
          onKeyDown={onKeyDownList}
        >
          {data?.items?.map((row, idx) => {
            const viStatus = translateStatus(row.stock_status);
            return (
              <div
                key={`${row.article_code || "code"}-${idx}`}
                role="option"
                aria-selected={activeIndex === idx}
                data-index={idx}
                style={{
                  ...styles.option,
                  ...(activeIndex === idx ? styles.optionActive : {}),
                }}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                <div style={styles.headerRow}>
                  <span style={styles.badge}>{viStatus}</span>
                  {row.update_date && (
                    <span style={styles.updated}>
                      Cập nhật: {fmtDate(row.update_date)}
                    </span>
                  )}
                </div>

                <dl style={styles.dl}>
                  <div style={styles.dItem}>
                    <dt style={styles.dt}>Nguồn gốc:</dt>
                    <dd style={styles.dd}>{row.origin || "—"}</dd>
                  </div>
                  <div style={styles.dItem}>
                    <dt style={styles.dt}>Mã vải:</dt>
                    <dd style={styles.dd}>{row.group_color || "—"}</dd>
                  </div>
                  <div style={styles.dItem}>
                    <dt style={styles.dt}>Bộ sưu tập:</dt>
                    <dd style={styles.dd}>{row.collection_name || "—"}</dd>
                  </div>
                  <div style={styles.dItem}>
                    <dt style={styles.dt}>Mã HD:</dt>
                    <dd style={{ ...styles.dd, ...styles.code }}>{row.article_code || "—"}</dd>
                  </div>
                  <div style={styles.dItem}>
                    <dt style={styles.dt}>Giá EXW:</dt>
                    <dd style={styles.dd}>{row.exw != null ? row.exw.toLocaleString("en-US") : "—"}</dd>
                  </div>
                  <div style={styles.dItem}>
                    <dt style={styles.dt}>MOQ:</dt>
                    <dd style={styles.dd}>{row.moq != null ? row.moq.toLocaleString("en-US") : "—"}</dd>
                  </div>
                  <div style={styles.dItem}>
                    <dt style={styles.dt}>Đơn vị tính (UOM):</dt>
                    <dd style={styles.dd}>{row.uom || "—"}</dd>
                  </div>
                  <div style={styles.dItem}>
                    <dt style={styles.dt}>Tình trạng kho:</dt>
                    <dd style={styles.dd}>{viStatus}</dd>
                  </div>
                </dl>
              </div>
            );
          })}
        </section>
      </main>
    </div>
  );
}

/** Translate common English stock statuses to Vietnamese during formatting. */
function translateStatus(s) {
  if (!s) return "—";
  const k = String(s).toLowerCase().trim();
  const map = {
    "in stock": "còn hàng",
    "available": "có sẵn",
    "low stock": "gần hết",
    "out of stock": "hết hàng",
    "on order": "đang đặt",
    "preorder": "đặt trước",
    "pre-order": "đặt trước",
    "discontinued": "ngừng sản xuất",
    "obsolete": "lỗi thời",
    "n/a": "không có",
    "na": "không có",
    "sell & delete": "bán hết & xóa",
    "sell and delete": "bán hết & xóa",
    "delete": "xóa",
  };
  return map[k] || s; // fallback to original if unknown
}

function fmtDate(d) {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return String(d);
  }
}

const styles = {
  container: { maxWidth: 900, margin: "0 auto", padding: "24px" },
  header: { marginBottom: 12 },
  h2: { margin: 0 },
  sub: { marginTop: 6, color: "#555" },
  searchWrap: { marginTop: 16, marginBottom: 8 },
  input: {
    width: "100%",
    padding: "12px 42px 12px 14px",
    borderRadius: 12,
    border: "1px solid #d0d7de",
    outline: "none",
    fontSize: "1rem",
    flex: "1 1 auto",
  },
  clearBtn: {
    position: "absolute",
    right: 112,
    top: 6,
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    cursor: "pointer",
  },
  searchBtn: {
    minWidth: 92,
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  searchBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  hint: { marginTop: 8, color: "#666", fontSize: ".9rem" },
  listbox: {
    marginTop: 8,
    display: "grid",
    gridAutoRows: "minmax(56px, auto)",
    gap: 10,
    maxHeight: "60vh",
    overflow: "auto",
    border: "1px solid #eee",
    borderRadius: 12,
    padding: 10,
    background: "#fff",
  },
  option: {
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid #f0f0f0",
    background: "#fafafa",
  },
  optionActive: { borderColor: "#0ea5e9", background: "#f0f9ff" },
  headerRow: { display: "flex", gap: 12, alignItems: "center", marginBottom: 8 },
  badge: { fontSize: ".78rem", background: "#eef2ff", color: "#3730a3", padding: "2px 8px", borderRadius: 999 },
  updated: { marginLeft: "auto", color: "#555", fontSize: ".85rem" },
  dl: {
    display: "grid",
    gridTemplateColumns: "160px 1fr",
    rowGap: 6,
    columnGap: 8,
    margin: 0,
  },
  dItem: { display: "contents" },
  dt: { fontWeight: 600, color: "#374151" },
  dd: { margin: 0, color: "#111827" },
  code: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
};
